import {Inject, Injectable, ViewChild} from "@angular/core";
import {ClientStore} from "../../../../stores/stores";
import {Subject} from "rxjs";
import {PaymentMethod} from "../../types/PaymentMethod";
import {ActivatedRoute, Router} from "@angular/router";
import {SetGlobalPayRequestJson, SetRmsRequestJson} from "../../../payments/+state/payment.actions";
import {IntegrationInitiatePaymentResponse} from "../../types/IntegrationPayment";
import {takeUntil} from "rxjs/operators";
import {getIntegrationPaymentStore} from "../../../payments/+state/payment.selectors";
import {LocalStorageService} from "../../../../local-storage.service";
import {PaymentsenseService} from "../../../payments/paymentsense.service";
import {WINDOW} from "../../../window-providers";
import {WhitelabelService} from "../../../../shared/whitelabel.service";
import {LocationService} from "../../../location.service";
import {PaymentService} from "../../../payments/payment.service";
import {StoreOrderService} from "../../../../stores/store-order/store-order.service";
import {TranslateService} from "@ngx-translate/core";
import {Store} from "@ngrx/store";
import {Event} from "../../../../stores/event";
import { CheckoutPaymentFormsComponent } from "../checkout-payment-forms/checkout-payment-forms.component";

@Injectable()
export abstract class CheckoutPaymentBaseComponent  {
  selectedStore: ClientStore;
  unsubscribe$: Subject<void> = new Subject<void>();
  protected paymentData: IntegrationInitiatePaymentResponse;
  protected queryParams: any;
  selectedPaymentMethod: string;

  protected constructor(protected route: ActivatedRoute,
                        protected store: Store<any>,
                        protected translate: TranslateService,
                        protected storeOrderService: StoreOrderService,
                        protected paymentService: PaymentService,
                        protected locationService: LocationService,
                        protected whitelabelService: WhitelabelService,
                        @Inject(WINDOW) protected window: Window,
                        protected paymentsenseService: PaymentsenseService,
                        protected router: Router,
                        protected storageService: LocalStorageService
  ) { }


  onInit(): void {
    this.queryParams = this.getParamsFromURL();
    this.subscribePaymentStoreState();
  }
  subscribePaymentStoreState() {
    this.store
    .select(getIntegrationPaymentStore)
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe(state => {
      let paymentState = state?.paymentState;
      this.handlePaymentState(paymentState.status, paymentState.requestJSON);
    });
  }

  handlePaymentState(paymentStatus: string, requestJson: any) {
    const evt : Event = {
      src: 'QrPaymentPayComponent',
      description: `Payment Status: ${paymentStatus}`
    };
    switch (paymentStatus) {
      case 'INITIATE_PAYMENT_SUCCESS':
      case 'INITIATE_ORDER_PAYMENT_SUCCESS':
        this.paymentData = requestJson;
        this.selectedPaymentMethod = requestJson.paymentMethod;
        this.handlePaymentRedirection();
        break;
      case 'INITIATE_PAYMENT_FAILURE':
      case 'INITIATE_ORDER_PAYMENT_FAILURE':
        this.onInitiatePaymentFailure(evt);
        break;
    }
  }



  private handlePaymentRedirection() {
    if (this.paymentData?.paymentFormParams?.providerPaymentType
      && this.paymentData?.paymentFormParams?.providerPaymentType === 'PHYSICAL'
      && this.selectedPaymentMethod !== PaymentMethod.CREDIT_CARD_VIVA) {
      this.setPhysicalPaymentDetails();
      this.redirectPhysicalPaymentProgress();
      return;
    }
    else if (this.selectedPaymentMethod === PaymentMethod.CREDIT_CARD_HOBEX || this.selectedPaymentMethod === PaymentMethod.CREDIT_CARD_RYFT) {
      this.setPaymentPayDetails();
      this.redirectPaymentPay();
      return;
    }

    else if (this.selectedPaymentMethod == PaymentMethod.CREDIT_CARD_APCOPAY) {
      this.getCheckoutPaymentFormsComponent().apcopayComponent.submitForm(this.paymentData);
    }

    else if (this.selectedPaymentMethod === PaymentMethod.CREDIT_CARD_VIVA) {
      const paymentServerUrl = this.paymentData?.paymentFormParams?.paymentServerUrl;
      if (this.paymentData?.paymentFormParams?.providerPaymentType === 'PHYSICAL') {
        this.setPhysicalPaymentDetails();
      }

      const endUrl = '/payment-routing/viva?ordercode=' + (this.paymentData.intentId || null)
        + '&url=' + this.getPaymentRoutingUrl()
        + '&tenantDomain=' + this.window.location.host
        + '&storeAliasName=' + this.paymentData?.paymentFormParams?.storeAlias
        + '&orderUuid=' + this.paymentData?.paymentFormParams?.paymentId
        + '&paymentTransactionId=' + this.paymentData?.paymentTransactionId
        + '&referenceType=' + this.paymentData?.referenceType
        + '&storeId=' + this.paymentData?.paymentFormParams?.storeId
        + '&providerPaymentType=' + this.paymentData?.paymentFormParams?.providerPaymentType
        + (paymentServerUrl ? '&paymentServerUrl=' + encodeURIComponent(paymentServerUrl) : '')
        + '&thankUrl=' + this.getPaymentRoutingThankYouUrl();

      this.router.navigateByUrl(endUrl);
    }

    else if (this.selectedPaymentMethod === PaymentMethod.CREDIT_CARD_FYGARO) {
      this.window.location.href = this.whitelabelService.getAdminHostURL() + '/payment-routing/fygaro?jwt=' + (this.paymentData?.paymentFormParams?.jwt || null)
        + '&url=' + this.getPaymentRoutingUrl()
        + '&storeAliasName=' + this.paymentData?.paymentFormParams?.storeAlias
        + '&orderUuid=' + this.paymentData?.paymentFormParams?.paymentId
        + '&paymentTransactionId=' + this.paymentData?.paymentTransactionId
        + '&referenceType=' + this.paymentData?.referenceType
        + '&storeId=' + this.paymentData?.paymentFormParams?.storeId
        + '&callbackUrl=' + this.paymentData?.paymentFormParams?.callbackUrl
        + '&checkoutUrl='+ this.paymentData.checkoutUrl
    }

    else if (this.selectedPaymentMethod === PaymentMethod.CREDIT_CARD_EPAY) {
      this.window.location.href = this.whitelabelService.getAdminHostURL() + '/payment-routing/epay?checkoutUrl=' + (this.paymentData?.checkoutUrl || null)
        + '&url=' + this.getPaymentRoutingUrl()
        + '&storeAliasName=' + this.paymentData?.paymentFormParams?.storeAlias
        + '&orderUuid=' + this.paymentData?.paymentFormParams?.orderId
        + '&paymentTransactionId=' + this.paymentData?.paymentTransactionId
        + '&referenceType=' + this.paymentData?.referenceType
        + '&storeId=' + this.paymentData?.paymentFormParams?.storeId
        + '&callbackUrl=' + this.paymentData?.paymentFormParams?.responseUrl
        + '&acquirerId='+ this.paymentData?.paymentFormParams?.acquirerId
        + '&merchantId='+ this.paymentData?.paymentFormParams?.merchantId
        + '&posId='+ this.paymentData?.paymentFormParams?.posId
        + '&user='+ this.paymentData?.paymentFormParams?.username
        + '&languageCode='+ this.paymentData?.paymentFormParams?.languageCode
        + '&merchantReference='+ this.paymentData?.paymentFormParams?.merchantReference
        + '&parameters='+ this.paymentData?.paymentFormParams?.parameters;
    }


    else if (this.selectedPaymentMethod === PaymentMethod.CREDIT_CARD_PAYMENTSENSE) {
      this.paymentsenseService.handlePaymentSenseFlow(this.store, this.paymentData, this.isStandalonePayment());
    }
    else if (this.selectedPaymentMethod === PaymentMethod.CREDIT_CARD_RMS) {
      this.store.dispatch(new SetRmsRequestJson(this.paymentData));
    }
    else if (this.selectedPaymentMethod === PaymentMethod.CREDIT_CARD_TRUSTPAYMENTS) {
      this.getCheckoutPaymentFormsComponent().trustpaymentComponent.submitForm(this.paymentData);
    }
    else if (this.selectedPaymentMethod === PaymentMethod.CREDIT_CARD_JCC) {
      this.onInitiateJccPayment();
      this.getCheckoutPaymentFormsComponent().jccComponent.submitForm(this.paymentData);
    }

    else if (this.selectedPaymentMethod === PaymentMethod.CREDIT_CARD_GLOBALPAY) {
      this.store.dispatch(new SetGlobalPayRequestJson(this.paymentData));
    } else {
      this.window.location.href = this.paymentData.checkoutUrl;
    }

  }

  private setPhysicalPaymentDetails() {
    const physicalPaymentDetails = {
      'orderId': this.paymentData.paymentFormParams.orderId,
      'storeId': this.paymentData.paymentFormParams.storeId,
      'paymentTransactionId': this.paymentData?.paymentTransactionId,
      'referenceType': this.paymentData?.referenceType,
      'isStandalonePayment': this.isStandalonePayment(),
      'redirectUrl': this.paymentData.paymentFormParams.responseUrl,
      'paymentMethod': this.paymentData?.paymentMethod,
      'paymentServerUrl': this.paymentData.paymentFormParams.paymentServerUrl,
      'provider': this.paymentData?.integrationProvider
    };
    this.storageService.setSavedState(JSON.stringify(physicalPaymentDetails), 'physicalPaymentDetails');
  }

  private setPaymentPayDetails() {
    const paymentPayDetails = {
      ... this.getPaymentDetails(),
      'requestJSON': this.paymentData,
      'isStandalonePayment': this.isStandalonePayment(),
      'selectedStore': this.selectedStore,
    }
    this.storageService.setSavedState(JSON.stringify(paymentPayDetails), 'paymentPayDetails');
  }

  private isStandalonePayment() {
    return this.paymentData?.referenceType === 'STANDALONE_PAYMENT';
  }

  getParamsFromURL() {
    let parameterName;
    let parameterValue;
    this.route.params.subscribe(val => {
      parameterName = Object.keys(val)[0];
      parameterValue = Object.values(val)[0];
    });
    return {
      parameterName,
      parameterValue
    };
  }

  abstract onInitiatePaymentFailure(evt: Event);
  abstract redirectPhysicalPaymentProgress();
  abstract redirectPaymentPay();
  abstract getPaymentRoutingUrl();
  abstract getPaymentRoutingThankYouUrl();
  abstract onInitiateJccPayment();
  abstract getPaymentDetails();
  abstract getCheckoutPaymentFormsComponent();
}
